import { getNutritionPlanDisplayName as getResolvedNutritionPlanDisplayName } from "./nutritionPlanNameUtils";

function resolvePlanDateValue(value) {
  if (!value) {
    return Number.NaN;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value?.toDate === "function") {
    const resolvedDate = value.toDate();
    return resolvedDate instanceof Date ? resolvedDate.getTime() : Number.NaN;
  }

  const parsedDate = new Date(value);
  return Number.isFinite(parsedDate.getTime()) ? parsedDate.getTime() : Number.NaN;
}

function comparePlansByMostRecent(leftPlan, rightPlan, language = "en") {
  const rightUpdatedAt = resolvePlanDateValue(rightPlan?.updatedAt);
  const leftUpdatedAt = resolvePlanDateValue(leftPlan?.updatedAt);

  if (rightUpdatedAt !== leftUpdatedAt) {
    const safeRightUpdatedAt = Number.isFinite(rightUpdatedAt) ? rightUpdatedAt : -Infinity;
    const safeLeftUpdatedAt = Number.isFinite(leftUpdatedAt) ? leftUpdatedAt : -Infinity;

    if (safeRightUpdatedAt !== safeLeftUpdatedAt) {
      return safeRightUpdatedAt - safeLeftUpdatedAt;
    }
  }

  const rightCreatedAt = resolvePlanDateValue(rightPlan?.createdAt);
  const leftCreatedAt = resolvePlanDateValue(leftPlan?.createdAt);

  if (rightCreatedAt !== leftCreatedAt) {
    const safeRightCreatedAt = Number.isFinite(rightCreatedAt) ? rightCreatedAt : -Infinity;
    const safeLeftCreatedAt = Number.isFinite(leftCreatedAt) ? leftCreatedAt : -Infinity;

    if (safeRightCreatedAt !== safeLeftCreatedAt) {
      return safeRightCreatedAt - safeLeftCreatedAt;
    }
  }

  return getNutritionPlanDisplayName(leftPlan, language).localeCompare(
    getNutritionPlanDisplayName(rightPlan, language),
    undefined,
    { sensitivity: "base" },
  );
}

function compareNumberDescending(leftPlan, rightPlan, field, language = "en") {
  const leftValue = Number(leftPlan?.[field] ?? 0);
  const rightValue = Number(rightPlan?.[field] ?? 0);

  if (rightValue !== leftValue) {
    return rightValue - leftValue;
  }

  return comparePlansByMostRecent(leftPlan, rightPlan, language);
}

export const NUTRITION_PLAN_SORT_KEYS = Object.freeze({
  UPDATED_AT_DESC: "updatedAtDesc",
  NAME_ASC: "nameAsc",
  CALORIES_DESC: "caloriesDesc",
  FATS_DESC: "fatsDesc",
  CARBS_DESC: "carbsDesc",
  PROTEIN_DESC: "proteinDesc",
});

export function getNutritionPlanDisplayName(nutritionPlan, language = "en") {
  return getResolvedNutritionPlanDisplayName(nutritionPlan, language);
}

export function filterNutritionPlansBySearchTerm(
  nutritionPlans,
  searchTerm = "",
  language = "en",
) {
  if (!Array.isArray(nutritionPlans)) {
    return [];
  }

  const normalizedSearchTerm =
    typeof searchTerm === "string" ? searchTerm.trim().toLowerCase() : "";

  if (!normalizedSearchTerm) {
    return nutritionPlans;
  }

  return nutritionPlans.filter((nutritionPlan) => {
    const name = getNutritionPlanDisplayName(nutritionPlan, language);

    return name.toLowerCase().includes(normalizedSearchTerm);
  });
}

export function sortNutritionPlansByMostRecent(
  nutritionPlans,
  language = "en",
) {
  return sortNutritionPlans(
    nutritionPlans,
    NUTRITION_PLAN_SORT_KEYS.UPDATED_AT_DESC,
    language,
  );
}

export function sortNutritionPlans(
  nutritionPlans,
  sortKey = NUTRITION_PLAN_SORT_KEYS.UPDATED_AT_DESC,
  language = "en",
) {
  if (!Array.isArray(nutritionPlans)) {
    return [];
  }

  return [...nutritionPlans].sort((leftPlan, rightPlan) => {
    switch (sortKey) {
      case NUTRITION_PLAN_SORT_KEYS.NAME_ASC:
        return getNutritionPlanDisplayName(leftPlan, language).localeCompare(
          getNutritionPlanDisplayName(rightPlan, language),
          undefined,
          { sensitivity: "base" },
        );
      case NUTRITION_PLAN_SORT_KEYS.CALORIES_DESC:
        return compareNumberDescending(leftPlan, rightPlan, "calories", language);
      case NUTRITION_PLAN_SORT_KEYS.FATS_DESC:
        return compareNumberDescending(leftPlan, rightPlan, "fats", language);
      case NUTRITION_PLAN_SORT_KEYS.CARBS_DESC:
        return compareNumberDescending(leftPlan, rightPlan, "carbs", language);
      case NUTRITION_PLAN_SORT_KEYS.PROTEIN_DESC:
        return compareNumberDescending(leftPlan, rightPlan, "protein", language);
      case NUTRITION_PLAN_SORT_KEYS.UPDATED_AT_DESC:
      default:
        return comparePlansByMostRecent(leftPlan, rightPlan, language);
    }
  });
}

export function deriveVisibleNutritionPlans(
  nutritionPlans,
  {
    searchTerm = "",
    sortKey = NUTRITION_PLAN_SORT_KEYS.UPDATED_AT_DESC,
    language = "en",
  } = {},
) {
  return sortNutritionPlans(
    filterNutritionPlansBySearchTerm(nutritionPlans, searchTerm, language),
    sortKey,
    language,
  );
}

export function buildNutritionPlanLibrarySearchResults(
  nutritionPlans,
  searchTerm = "",
  language = "en",
) {
  return deriveVisibleNutritionPlans(nutritionPlans, {
    searchTerm,
    sortKey: NUTRITION_PLAN_SORT_KEYS.UPDATED_AT_DESC,
    language,
  });
}
